<template>
  <ag-index-page
    :view="view"
    :columns="columns"
    create-to="/admin/clients/new"
    create-label="Crear cliente"
    create-icon="mdi-plus-box"
    remove-title="Eliminar cliente"
  />
</template>

<script setup lang="ts">
import type { Client, UserRole } from "bancojs";
import { banco } from "@/api/client";
import { useIndexView } from "@/composables/useIndexView";
import AgIndexPage from "@/components/AgIndexPage.vue";
import type { AgDataTableOptions } from "@/components/table/types";
import { roleLabel, stateLabel } from "@/utils/labels";

const view = useIndexView<Client, "id" | "username" | "name" | "email">({
  title: "Índice de clientes",
  rowKey: "id",
  fetchPage: (params) => banco.admin.clients.find(params),
  remove: (item) => banco.admin.clients.destroy(item.id),
  removedMessage: (item) => `Se eliminó al cliente ID ${item.id}.`,
  filters: [
    { field: "id", label: "ID" },
    { field: "username", label: "Usuario" },
    { field: "name", label: "Nombre" },
    { field: "email", label: "Email" },
  ],
  confirm: {
    expected: (item) => String(item.id),
    label: "ID Cliente",
    hint: "Para confirmar ingresa el <b>ID</b> del cliente a eliminar.",
    error: "El ID de cliente no es correcto.",
    warning: (item) =>
      `Esta acción eliminará al cliente ${item.id}, junto con todas sus llaves y certificados.`,
  },
});

const columns: AgDataTableOptions[] = [
  { header: { text: "#", value: "$index", width: 60, sortable: false }, row: { type: "INDEX" } },
  {
    header: { text: "ID", value: "id", class: "text-no-wrap" },
    row: { type: "LINK", to: "/admin/clients/{id}/edit" },
  },
  { header: { text: "Usuario", value: "username", class: "text-no-wrap" }, row: { type: "TEXT" } },
  // La cabecera decía "Name" en inglés, la única de todas las tablas.
  { header: { text: "Nombre", value: "name", class: "text-no-wrap" }, row: { type: "TEXT" } },
  { header: { text: "Email", value: "email", class: "text-no-wrap" }, row: { type: "TEXT" } },
  {
    header: { text: "Estado", value: "state", class: "text-no-wrap" },
    row: {
      type: "CHIP",
      label: true,
      color: { ACTIVE: "success", INACTIVE: "error" },
      transform: (val) => stateLabel(val),
    },
  },
  {
    header: { text: "Roles", value: "roles", class: "text-no-wrap", sortable: false },
    row: {
      type: "CHIP",
      label: true,
      color: {},
      transform: (roles) => String((roles as UserRole[])?.length ?? 0),
      tooltip: true,
      tooltipContent: (roles) =>
        ((roles as UserRole[]) ?? []).map((r) => `<div>${roleLabel(r)}</div>`).join(""),
    },
  },
  {
    header: { text: "", value: "$options", class: "text-no-wrap", sortable: false },
    row: {
      type: "OPTIONS",
      options: [
        { label: "Editar", icon: "mdi-pencil", link: "/admin/clients/{id}/edit" },
        {
          label: "Eliminar",
          icon: "mdi-delete",
          action: (item) => view.openRemove(item as Client),
        },
      ],
    },
  },
];
</script>
